import { AggregatedMetricsTableData, FrameworkRefs, useMaterialStandardId } from 'containers/Esrs';
import {
  EsrsAssessmentDocument_,
  MaterialMetricsPerDisclosureDocument_,
  MaterialMetricsPerDisclosureQuery_,
  ReportingUnitsMetricsPerDisclosureDocument_,
  useMaterialMetricsPerDisclosureQuery,
  useUpsertMaterialMetricsMutation,
  GetRequiredBuMaterialMetricsDocument_,
  GetRequiredMaterialMetricsDocument_,
  useGetRequiredDataCollectionMaterialMetricsQuery,
  useGetMetricTagsDetailForSubsidiariesQuery,
  useGetParentEsrsAssessmentIdQuery,
  GetOptimizedMetricsDrDocument_,
} from 'models';
import { useEffect, useMemo } from 'react';
import {
  DataCollectionLevel,
  MaterialMetricPerDisclosure,
  MetricTagDetailForSubsidiary,
  TableMetricData,
} from './DataCollection.d';
import { uniq } from 'lodash';
import { useParams } from 'react-router-dom';

type Metric =
  MaterialMetricsPerDisclosureQuery_['assessableMetrics'][0]['childrenMetrics'][0]['childMetric'];
type ParentMetric = MaterialMetricsPerDisclosureQuery_['assessableMetrics'][0];

type PartialAssessableMetric = Omit<MaterialMetricPerDisclosure, 'childrenMetrics'>;
export interface MetricPerDisclosureMaybeHasChild extends PartialAssessableMetric {
  childrenMetrics?: { childMetric?: MetricPerDisclosureMaybeHasChild | null }[];
}

export const useMaterialStandardDataCollection = (
  standardRef: string,
  esrsAssessmentId: string
) => {
  const {
    companyAssessmentId: companyStandardId,
    parentAssessmentId: parentStandardId,
    loading: materialStandardIdLoading,
  } = useMaterialStandardId(standardRef, esrsAssessmentId);

  const { data: parentEsrsAssessmentData, loading: parentEsrsAssessmentDataLoading } =
    useGetParentEsrsAssessmentIdQuery({
      variables: {
        esrsAssessmentId,
      },
      skip: !esrsAssessmentId,
    });

  const parentEsrsAssessmentId = useMemo(
    () => parentEsrsAssessmentData?.esrsAssessment?.parentAssessmentId,
    [parentEsrsAssessmentData]
  );

  return {
    companyStandardId,
    parentStandardId,
    parentEsrsAssessmentId,
    loading: parentEsrsAssessmentDataLoading || materialStandardIdLoading,
  };
};

export const useMaterialMetricsPerDR = ({
  disclosureRequirementRef,
  companyStandardId,
  parentStandardId,
  isGroup,
  isStandardMandatory,
  frameworkRef,
}: {
  disclosureRequirementRef: string;
  companyStandardId: string;
  parentStandardId: string;
  isGroup: boolean;
  isStandardMandatory: boolean;
  frameworkRef: FrameworkRefs;
}) => {
  const [upsertMaterialMetric] = useUpsertMaterialMetricsMutation();
  const { esrsAssessmentId } = useParams();
  const { data: requiredMaterialMetricsData, loading: requiredmmLoading } =
    useGetRequiredDataCollectionMaterialMetricsQuery({
      variables: {
        disclosureRequirementRef: disclosureRequirementRef,
        materialStandardId: companyStandardId,
        parentStandardId: parentStandardId || companyStandardId,
        frameworkRef: frameworkRef,
      },
      skip: !disclosureRequirementRef || !companyStandardId || !frameworkRef,
    });

  const { data: metricTagDetailData, loading: metricTagDetailLoading } =
    useGetMetricTagsDetailForSubsidiariesQuery({
      variables: {
        assessmentId: esrsAssessmentId,
        drRef: disclosureRequirementRef,
        frameworkRef: frameworkRef,
      },
      skip: !isGroup || !esrsAssessmentId || !disclosureRequirementRef || !frameworkRef,
    });

  const requiredMetrics = useMemo(
    () =>
      uniq(requiredMaterialMetricsData?.esrs_MaterialMetric?.map((mm) => mm.metric.reference)) ??
      [],
    [requiredMaterialMetricsData]
  );

  const tagDetailForSubsidiaries = useMemo(() => {
    const tempTagDetailForSubsidiaries: { [key: string]: MetricTagDetailForSubsidiary } = {};
    metricTagDetailData?.esrs_MaterialMetric?.forEach((met) => {
      const requiredTagsWithValues = met.materialMetricTags.filter((materialMetricTag) => {
        for (const metricTag of met.metric.tags) {
          if (metricTag.type === materialMetricTag.tagType) {
            return true;
          }
        }
        return false;
      });
      const text = met.materialMetricTags.length === 0 ? 'Required' : 'Configured';
      const ratio =
        met.materialMetricTags.length === 0
          ? `(${met.metric.tags.length})`
          : `(${requiredTagsWithValues.length}/${met.metric.tags.length})`;
      const variant = met.materialMetricTags.length !== 0 ? 'info' : 'warning';
      if (tempTagDetailForSubsidiaries?.hasOwnProperty(met.metricRef)) {
        tempTagDetailForSubsidiaries[met.metricRef]['subsidiaryDetail'].push({
          companyName: met.materialStandard.esrsAssessment.company.name,
          companyId: met.materialStandard.esrsAssessment.company.id,
          status: {
            text,
            ratio,
          },
          assessmentId: met.materialStandard.esrsAssessment.id,
          variant,
        });
        tempTagDetailForSubsidiaries[met.metricRef]['configuredCount'] +=
          met.metric.tags.length === requiredTagsWithValues.length ? 1 : 0;
      } else {
        tempTagDetailForSubsidiaries[met.metricRef] = {
          subsidiaryDetail: [
            {
              companyName: met.materialStandard.esrsAssessment.company.name,
              companyId: met.materialStandard.esrsAssessment.company.id,
              status: {
                text,
                ratio,
              },
              assessmentId: met.materialStandard.esrsAssessment.id,
              variant,
            },
          ],
          configuredCount: met.metric.tags.length === requiredTagsWithValues.length ? 1 : 0,
        };
      }
    });
    return tempTagDetailForSubsidiaries;
  }, [metricTagDetailData]);

  const { data: disclosureMetricData, loading: metricDataLoading } =
    useMaterialMetricsPerDisclosureQuery({
      variables: {
        disclosureRequirementRef,
        companyStandardId: companyStandardId,
        parentStandardId: parentStandardId || companyStandardId,
        requiredMetrics: requiredMetrics,
        frameworkRef: frameworkRef,
      },
      skip:
        !companyStandardId || !disclosureRequirementRef || !requiredMetrics.length || !frameworkRef,
    });

  const checkIfChildNeedsMaterialMetric = (metric: Metric, parent?: ParentMetric) => {
    const hasMaterialMetric = !!metric?.materialMetrics?.length;
    const defaultDataCollection = isGroup
      ? isStandardMandatory
        ? DataCollectionLevel.group
        : DataCollectionLevel.subsidiaries
      : DataCollectionLevel.company;
    if (!hasMaterialMetric) {
      const parentMaterialMetric = parent?.materialMetrics.find(
        (mm) => mm.materialStandardId === companyStandardId
      );

      // create a material metric for it
      upsertMaterialMetric({
        variables: {
          objects: [
            {
              frequency: parentMaterialMetric?.frequency ?? 'Yearly',
              dataCollection: parentMaterialMetric?.dataCollection ?? defaultDataCollection,
              materialStandardId: companyStandardId,
              metricRef: metric?.reference,
            },
          ],
        },
        refetchQueries: [
          GetOptimizedMetricsDrDocument_,
          ReportingUnitsMetricsPerDisclosureDocument_,
          GetRequiredMaterialMetricsDocument_,
          GetRequiredBuMaterialMetricsDocument_,
          MaterialMetricsPerDisclosureDocument_,
          EsrsAssessmentDocument_,
        ],
      }).catch((error) => {
        console.error('Failed to add material metric for missing child', error);
      });
    }
    if (!!metric?.childrenMetrics?.length) {
      metric?.childrenMetrics.forEach((childMetric) => {
        checkIfChildNeedsMaterialMetric(childMetric.childMetric as Metric, metric as ParentMetric);
      });
    }
  };

  useEffect(() => {
    disclosureMetricData?.assessableMetrics.forEach((metric) => {
      checkIfChildNeedsMaterialMetric(metric as Metric);
    });
  }, [disclosureMetricData]);

  // Sort metrics based on framework
  const sortMetricsByFramework = (metrics: MaterialMetricPerDisclosure[]) => {
    return metrics.sort((a, b) => {
      const aFramework = a.requirement.frameworkRef;
      const bFramework = b.requirement.frameworkRef;

      if (aFramework === frameworkRef && bFramework !== frameworkRef) return -1;
      if (aFramework !== frameworkRef && bFramework === frameworkRef) return 1;

      return 0;
    });
  };

  const disclosureMetrics = useMemo(() => {
    const assessableMetrics = disclosureMetricData?.assessableMetrics ?? [];
    const orderedByFramework = sortMetricsByFramework([...assessableMetrics]);
    return orderedByFramework;
  }, [disclosureMetricData]);

  return {
    disclosureMetrics,
    requiredMetrics,
    tagDetailForSubsidiaries,
    metricDataLoading: metricDataLoading || requiredmmLoading || metricTagDetailLoading,
  };
};

export const getAllMaterialMetricChildren = (
  companyStandardId: string,
  metric?: TableMetricData
): any => {
  const childrenMetrics = metric?.childrenMetrics;

  if (!childrenMetrics) {
    return [];
  }

  const data = childrenMetrics.flatMap((childMetric) => {
    const childData = getAllMaterialMetricChildren(
      companyStandardId,
      childMetric?.childMetric as TableMetricData
    );
    const materialMetric = childMetric.childMetric?.materialMetrics.find(
      (mm) => mm.materialStandardId === companyStandardId
    );
    return [
      {
        id: materialMetric?.id,
        metricRef: childMetric.childMetric?.reference,
        materialStandardId: companyStandardId,
        metricType: childMetric.childMetric?.metricType,
        isMaterial: materialMetric?.isMaterial,
      },
      ...childData,
    ];
  });

  return data;
};

export const getFlattenedMaterialMetrics = (
  metrics: TableMetricData[],
  companyStandardId: string
): any => {
  return metrics
    .flatMap((metric) => {
      const materialMetric = metric.materialMetrics.find(
        (mm) => mm.materialStandardId === companyStandardId
      );
      return [
        {
          id: materialMetric?.id,
          metricRef: metric.reference,
          materialStandardId: companyStandardId,
          metricType: metric.metricType,
          isMaterial: materialMetric?.isMaterial,
        },
        getAllMaterialMetricChildren(companyStandardId, metric),
      ].flat();
    })
    .filter((value, index, self) => index === self.findIndex((t) => t.id === value.id));
};

export const hasDataCollectionGroup = (metric: TableMetricData): boolean => {
  const isCollectionGroup = metric.materialMetrics?.[0]?.dataCollection === 'group';
  if (!metric?.childrenMetrics?.length) return isCollectionGroup;
  return (
    isCollectionGroup ||
    metric.childrenMetrics.some(
      (child) => child.childMetric && hasDataCollectionGroup(child.childMetric as TableMetricData)
    )
  );
};

export const isDataCollectionMatching = (
  metric: AggregatedMetricsTableData['metric'],
  dataCollection: string
): boolean => {
  const isMatching = metric?.materialMetrics?.[0]?.dataCollection === dataCollection;
  if (!metric?.childrenMetrics?.length) return isMatching;
  return (
    false ||
    metric?.childrenMetrics?.every(
      (child) => child.childMetric && isDataCollectionMatching(child.childMetric, dataCollection)
    )
  );
};

export const metricsWithRequiredTags = (
  metricList: MaterialMetricPerDisclosure[],
  isGroup: boolean
): MaterialMetricPerDisclosure[] => {
  const visitedMetric: { [key: string]: boolean } = {};
  const requiredMetricList: MaterialMetricPerDisclosure[] = [];
  const addMetricToList = (metric: MaterialMetricPerDisclosure): void => {
    if (visitedMetric.hasOwnProperty(metric?.reference)) {
      return;
    }
    visitedMetric[metric?.reference] = true;
    const tagList = [...(metric?.adminPanelTags ?? [])];
    if (!tagList.length) {
      if (metric?.childrenMetrics?.length) {
        metric.childrenMetrics.forEach(
          (met) => met.childMetric && addMetricToList(met.childMetric)
        );
      }
      return;
    }
    if (tagList.some((tag) => !tag.isOptional)) {
      if (metric?.childrenMetrics?.length) {
        const dataCollection =
          metric.childrenMetrics[0]?.childMetric?.materialMetrics?.[0]?.dataCollection;
        if (!isGroup || dataCollection === 'group') {
          requiredMetricList.push(metric);
        }
        return;
      }
      const dataCollection = metric?.materialMetrics?.[0]?.dataCollection;
      if (!isGroup || dataCollection === 'group') {
        requiredMetricList.push(metric);
      }
    }
  };

  metricList.forEach((met) => addMetricToList(met));
  return requiredMetricList;
};

export const hasCompletedTags = (
  metric: MetricPerDisclosureMaybeHasChild,
  isRequired?: boolean
): boolean => {
  const tags = metric.adminPanelTags.filter((tag) => (isRequired ? !tag.isOptional : true));

  const hasCompletedAllTags = metric.materialMetrics.some((mm) =>
    tags.every((tag) =>
      mm.materialMetricTags.some(
        (matTag) => matTag.tagType === tag.type && matTag.materialTagValues.length > 0
      )
    )
  );

  if (!metric.childrenMetrics?.length) return hasCompletedAllTags;

  return (
    hasCompletedAllTags &&
    metric.childrenMetrics.every(
      (child) => child.childMetric && hasCompletedTags(child.childMetric, isRequired)
    )
  );
};
